import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';

// SVG Icons (unchanged)
const CloseIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);
const PlusIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);
const TrashIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);
const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"></path>
  </svg>
);
const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3"></path>
  </svg>
);
const SaveIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"></path>
    <polyline points="17 21 17 13 7 13 7 21"></polyline>
    <polyline points="7 3 7 8 15 8"></polyline>
  </svg>
);
const AutofillIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);

const API_URL = '/api';

// Helper function to convert a string to uppercase
const toUpperCase = (value) => {
  if (typeof value !== 'string') return value;
  return value.toUpperCase();
};

// Status options
const STATUS_OPTIONS = [
  { value: '1', label: 'ACTIVE' },
  { value: '0', label: 'INACTIVE' },
];
// Inventory flag options
const INVENTORY_FLAG_OPTIONS = [
  { value: 'yes', label: 'YES' },
  { value: 'no', label: 'NO' },
];

const initialHeader = {
  stockInId: '',
  stockInDate: new Date().toISOString().split('T')[0],
  addedByUserId: '',
  addedByUserName: '',
  note: '',
  status: '1',
  inventoryFlag: 'yes',
  totalQuantity: '0',
  branchCode: '',
  department_id: "",
};

const initialItem = {
  itemCode: '',
  itemName: '',
  uom: '',
  quantityAdded: '',
  rate: '',
  value: '0',
  locationCode: '',
  department_id: "",
  batchNumber: "",
  expiryDate: "",
  allowBatch: false,
  savedToTable: false, // New field to track if item is saved to table
   // Add these new tracking fields
  productTrackingType: 0, // 0: None, 1: Batch, 2: Serial
  batches: [], // For batch tracking
  serialNumbers: [], // For serial tracking
  showBatchModal: false,
  showSerialModal: false,
};

// ERP Command Constants
const ERP_COMMANDS = {
  SAVE_POPUP_ITEMS: 'SAVE_POPUP_ITEMS',
  LOAD_SAVED_ITEMS: 'LOAD_SAVED_ITEMS',
  DELETE_SAVED_ITEMS: 'DELETE_SAVED_ITEMS',
  AUTOFILL_ITEMS: 'AUTOFILL_ITEMS' // New command for autofilling items
};

// Custom Product Dropdown Component with Search
const ProductSearchDropdown = ({ 
  products, 
  value, 
  onChange, 
  onMultiSelectTrigger,
  error 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownListRef = useRef(null);

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width
      });
    }
  }, [isOpen]);

  // Handle window resize and scroll to update position
  useEffect(() => {
    if (!isOpen) return;
    
    const handleUpdatePosition = () => {
      if (inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect();
        setPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width
        });
      }
    };
    
    window.addEventListener('resize', handleUpdatePosition);
    window.addEventListener('scroll', handleUpdatePosition);
    
    return () => {
      window.removeEventListener('resize', handleUpdatePosition);
      window.removeEventListener('scroll', handleUpdatePosition);
    };
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && dropdownListRef.current && highlightedIndex >= 0) {
      const items = dropdownListRef.current.querySelectorAll('.dropdown-item');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Filter products based on search term
  const filteredProducts = useMemo(() => {
    if (!searchTerm) return products;
    const term = searchTerm.toLowerCase();
    return products.filter(product => 
      product.ProductCode.toLowerCase().includes(term) || 
      product.ProductName.toLowerCase().includes(term)
    );
  }, [products, searchTerm]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        setHighlightedIndex(0);
        return;
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev < filteredProducts.length - 1) {
            return prev + 1;
          } else if (filteredProducts.length > 0) {
            return 0; // Loop back to first item
          }
          return prev;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev > 0) {
            return prev - 1;
          } else if (filteredProducts.length > 0) {
            return filteredProducts.length - 1; // Loop to last item
          }
          return prev;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredProducts.length) {
          handleSelectProduct(filteredProducts[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      case 'p':
        if (e.ctrlKey) {
          e.preventDefault();
          setIsOpen(false);
          onMultiSelectTrigger();
        }
        break;
      default:
        break;
    }
  };

  const handleSelectProduct = (product) => {
    onChange(product.ProductCode);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Get selected product name
  const selectedProduct = products.find(p => p.ProductCode === value);

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className={`w-40 border rounded-lg px-2 py-1 cursor-pointer flex justify-between items-center ${
          error ? 'border-red-400' : 'border-[#E2E8F0]'
        } ${isOpen ? 'ring-1 ring-[#BDDDFC]' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        ref={inputRef}
      >
        <span className="truncate">
          {selectedProduct ? selectedProduct.ProductName : 'Select Product'}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      
      {isOpen && (
        <div 
          className="fixed z-[9999] bg-white border border-[#E2E8F0] rounded-lg shadow-lg"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            width: `${position.width}px`,
          }}
        >
          <div className="p-2 border-b border-[#E2E8F0]">
            <input
              type="text"
              placeholder="Search by ID or name..."
              className="w-full px-2 py-1 border border-[#E2E8F0] rounded focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          
          <div 
            className="max-h-60 overflow-y-auto" 
            ref={dropdownListRef}
          >
            {filteredProducts.length > 0 ? (
              filteredProducts.map((product, index) => (
                <div
                  key={product.ProductCode}
                  className={`dropdown-item px-3 py-2 cursor-pointer hover:bg-[#F1F5F9] ${
                    highlightedIndex === index ? 'bg-[#BDDDFC]' : ''
                  } ${value === product.ProductCode ? 'bg-[#E6F0FF]' : ''}`}
                  onClick={() => handleSelectProduct(product)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="font-medium">{product.ProductName}</div>
                  <div className="text-xs text-gray-500">{product.ProductCode}</div>
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500">No products found</div>
            )}
          </div>
          
          <div className="p-2 border-t border-[#E2E8F0] text-xs text-gray-500">
            Press Ctrl+P for multi-select
          </div>
        </div>
      )}
      
      {error && <span className="text-xs text-red-500">*</span>}
    </div>
  );
};

// Custom Warehouse Dropdown Component with Search
const WarehouseSearchDropdown = ({ 
  warehouses, 
  value, 
  onChange, 
  onMultiSelectTrigger,
  error 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownListRef = useRef(null);

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width
      });
    }
  }, [isOpen]);

  // Handle window resize and scroll to update position
  useEffect(() => {
    if (!isOpen) return;
    
    const handleUpdatePosition = () => {
      if (inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect();
        setPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width
        });
      }
    };
    
    window.addEventListener('resize', handleUpdatePosition);
    window.addEventListener('scroll', handleUpdatePosition);
    
    return () => {
      window.removeEventListener('resize', handleUpdatePosition);
      window.removeEventListener('scroll', handleUpdatePosition);
    };
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && dropdownListRef.current && highlightedIndex >= 0) {
      const items = dropdownListRef.current.querySelectorAll('.dropdown-item');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Filter warehouses based on search term
  const filteredWarehouses = useMemo(() => {
    if (!searchTerm) return warehouses;
    const term = searchTerm.toLowerCase();
    return warehouses.filter(warehouse => 
      warehouse.LocationCode.toLowerCase().includes(term) || 
      warehouse.LocationName.toLowerCase().includes(term)
    );
  }, [warehouses, searchTerm]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        setHighlightedIndex(0);
        return;
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev < filteredWarehouses.length - 1) {
            return prev + 1;
          } else if (filteredWarehouses.length > 0) {
            return 0; // Loop back to first item
          }
          return prev;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev > 0) {
            return prev - 1;
          } else if (filteredWarehouses.length > 0) {
            return filteredWarehouses.length - 1; // Loop to last item
          }
          return prev;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredWarehouses.length) {
          handleSelectWarehouse(filteredWarehouses[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      case 'w':
        if (e.ctrlKey) {
          e.preventDefault();
          setIsOpen(false);
          onMultiSelectTrigger();
        }
        break;
      default:
        break;
    }
  };

  const handleSelectWarehouse = (warehouse) => {
    onChange(warehouse.LocationCode);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Get selected warehouse name
  const selectedWarehouse = warehouses.find(w => w.LocationCode === value);

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className={`w-24 border rounded-lg px-2 py-1 cursor-pointer flex justify-between items-center ${
          error ? 'border-red-400' : 'border-[#E2E8F0]'
        } ${isOpen ? 'ring-1 ring-[#BDDDFC]' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        ref={inputRef}
      >
        <span className="truncate">
          {selectedWarehouse ? selectedWarehouse.LocationName : 'Select'}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      
      {isOpen && (
        <div 
          className="fixed z-[9999] bg-white border border-[#E2E8F0] rounded-lg shadow-lg"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            width: `${position.width}px`,
          }}
        >
          <div className="p-2 border-b border-[#E2E8F0]">
            <input
              type="text"
              placeholder="Search by code or name..."
              className="w-full px-2 py-1 border border-[#E2E8F0] rounded focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          
          <div 
            className="max-h-60 overflow-y-auto" 
            ref={dropdownListRef}
          >
            {filteredWarehouses.length > 0 ? (
              filteredWarehouses.map((warehouse, index) => (
                <div
                  key={warehouse.LocationCode}
                  className={`dropdown-item px-3 py-2 cursor-pointer hover:bg-[#F1F5F9] ${
                    highlightedIndex === index ? 'bg-[#BDDDFC]' : ''
                  } ${value === warehouse.LocationCode ? 'bg-[#E6F0FF]' : ''}`}
                  onClick={() => handleSelectWarehouse(warehouse)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="font-medium">{warehouse.LocationName}</div>
                  <div className="text-xs text-gray-500">{warehouse.LocationCode}</div>
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500">No warehouses found</div>
            )}
          </div>
          
          <div className="p-2 border-t border-[#E2E8F0] text-xs text-gray-500">
            Press Ctrl+W for multi-select
          </div>
        </div>
      )}
      
      {error && <span className="text-xs text-red-500">*</span>}
    </div>
  );
};

// Multi-Select Product Modal Component
const ProductMultiSelectModal = ({ 
  open, 
  onClose, 
  products, 
  onConfirm,
  onSaveToTable,
  onAutofill 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isSaving, setIsSaving] = useState(false);
  const listRef = useRef(null);
  const searchInputRef = useRef(null);
  const tableBodyRef = useRef(null);

  // Filter products based on search term
  const filteredProducts = useMemo(() => {
    if (!searchTerm) return products;
    const term = searchTerm.toLowerCase();
    return products.filter(product => {
      return (
        (product.ProductCode && product.ProductCode.toLowerCase().includes(term)) || 
        (product.ProductName && product.ProductName.toLowerCase().includes(term)) ||
        (product.Status && product.Status.toLowerCase().includes(term)) ||
        (product.CategoryName && product.CategoryName.toLowerCase().includes(term)) ||
        (product.UOMName && product.UOMName.toLowerCase().includes(term)) ||
        (product.Description && product.Description.toLowerCase().includes(term)) ||
        (product.BranchName && product.BranchName.toLowerCase().includes(term)) ||
        (product.DepartmentName && product.DepartmentName.toLowerCase().includes(term)) ||
        (product.AllowProductNegativeInventory !== undefined && 
         (product.AllowProductNegativeInventory ? 'yes' : 'no').includes(term)) ||
        (product.AllowBatchOnProduct !== undefined && 
         (product.AllowBatchOnProduct ? 'yes' : 'no').includes(term)) ||
        (product.ProductType && product.ProductType.toLowerCase().includes(term)) ||
        (product.ProductionBOMType && product.ProductionBOMType.toLowerCase().includes(term))
      );
    });
  }, [products, searchTerm]);

  // Check if all filtered products are selected
  const allFilteredSelected = useMemo(() => {
    if (filteredProducts.length === 0) return false;
    return filteredProducts.every(product => selectedProducts.includes(product.ProductCode));
  }, [filteredProducts, selectedProducts]);

  // Check if some filtered products are selected (for indeterminate state)
  const someFilteredSelected = useMemo(() => {
    if (filteredProducts.length === 0) return false;
    return filteredProducts.some(product => selectedProducts.includes(product.ProductCode)) && !allFilteredSelected;
  }, [filteredProducts, selectedProducts, allFilteredSelected]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (open && tableBodyRef.current && highlightedIndex >= 0) {
      const items = tableBodyRef.current.querySelectorAll('.product-row');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex, open]);

  // Reset and focus when modal opens
  useEffect(() => {
    if (open) {
      setSearchTerm('');
      setSelectedProducts([]);
      setHighlightedIndex(-1);
      
      // Focus search input after a short delay
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }
  }, [open]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    // === Global Ctrl+S to trigger autofill ===
    if (e.ctrlKey && e.key.toLowerCase() === 's') {
      e.preventDefault(); // Prevent browser's default "Save Page" behavior
      handleAutofill();
      return;
    }

    // Handle search input keys
    if (e.target === searchInputRef.current) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex(0);
        // Move focus to the table body
        setTimeout(() => {
          if (tableBodyRef.current) {
            tableBodyRef.current.focus();
          }
        }, 0);
        return;
      }
      
      // Handle Enter in search input - select topmost suggestion
      if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredProducts.length > 0) {
          // Always select the first product (index 0) when pressing Enter in search input
          const firstProduct = filteredProducts[0];
          toggleProductSelection(firstProduct.ProductCode);
          // Keep focus in search input so user can continue searching
          searchInputRef.current.focus();
        }
      }
      return;
    }

    // Handle table body navigation
    if (e.target === tableBodyRef.current) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex(prev => {
            if (prev < filteredProducts.length - 1) {
              return prev + 1;
            } else if (filteredProducts.length > 0) {
              return 0; // Loop back to first item
            }
            return prev;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex(prev => {
            if (prev > 0) {
              return prev - 1;
            } else if (filteredProducts.length > 0) {
              return filteredProducts.length - 1; // Loop to last item
            }
            return prev;
          });
          break;
        case ' ':
          e.preventDefault();
          if (highlightedIndex >= 0 && highlightedIndex < filteredProducts.length) {
            toggleProductSelection(filteredProducts[highlightedIndex].ProductCode);
          }
          break;
        case 'Enter':
          e.preventDefault();
          if (e.ctrlKey) {
            // Ctrl+Enter for autofill
            handleAutofill();
          } else if (highlightedIndex >= 0 && highlightedIndex < filteredProducts.length) {
            // Regular Enter for selecting highlighted product
            toggleProductSelection(filteredProducts[highlightedIndex].ProductCode);
          }
          break;
        case 'Escape':
          onClose();
          break;
        default:
          break;
      }
    }
  };
  
  // Toggle product selection
  const toggleProductSelection = (productCode) => {
    setSelectedProducts(prev => 
      prev.includes(productCode)
        ? prev.filter(code => code !== productCode)
        : [...prev, productCode]
    );
  };

  // Handle select all filtered products
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // Add all filtered products to selection
      const allProductCodes = filteredProducts.map(p => p.ProductCode);
      setSelectedProducts(prev => [...new Set([...prev, ...allProductCodes])]);
    } else {
      // Remove all filtered products from selection
      const filteredProductCodes = filteredProducts.map(p => p.ProductCode);
      setSelectedProducts(prev => prev.filter(code => !filteredProductCodes.includes(code)));
    }
  };

  // Handle clear all selection
  const handleClearAll = () => {
    setSelectedProducts([]);
  };

  // Handle confirm selection
  const handleConfirm = () => {
    onConfirm(selectedProducts);
    onClose();
  };

  // Handle save to table
  const handleSaveToTable = async () => {
    if (selectedProducts.length === 0) return;
    
    setIsSaving(true);
    try {
      await onSaveToTable(selectedProducts);
      alert('Items saved to table successfully!');
    } catch (error) {
      console.error('Error saving to table:', error);
      alert('Failed to save items to table');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Handle autofill
  const handleAutofill = () => {
    if (selectedProducts.length === 0) return;
    
    try {
      const result = onAutofill(selectedProducts);
      
      if (result && result.success) {
        // Success - modal will close in finally block
      } else {
        alert(result?.error || 'Error autofilling table. Please try again.');
      }
    } catch (error) {
      console.error('Error during autofill:', error);
      alert('Error autofilling table. Please try again.');
    } finally {
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-2xl w-full  h-[100vh] flex flex-col border border-gray-200">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold text-gray-800">Select Multiple Products</h3>
            <button 
              onClick={onClose} 
              className="text-gray-500 hover:text-gray-700 transition-colors p-1 rounded-full hover:bg-gray-200"
            >
              <CloseIcon size={24} />
            </button>
          </div>
        </div>
        
        <div className="p-5 border-b border-gray-200 bg-white">
          <div className="relative">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by Product Code, Name, Status, Category, UOM, Description, Branch, Department, etc..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base shadow-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <div className="absolute right-3 top-3 text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          <div className="mt-3 text-sm text-gray-600 bg-blue-50 p-3 rounded-lg border border-blue-100">
            <div className="font-medium text-blue-800 mb-1">Keyboard Shortcuts:</div>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center px-2 py-1 bg-white rounded-md text-xs font-medium text-gray-700 border border-gray-200">
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">↑</kbd>
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">↓</kbd>
                <span className="ml-1">Navigate</span>
              </span>
              <span className="inline-flex items-center px-2 py-1 bg-white rounded-md text-xs font-medium text-gray-700 border border-gray-200">
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">Space</kbd>
                <span className="ml-1">Select</span>
              </span>
              <span className="inline-flex items-center px-2 py-1 bg-white rounded-md text-xs font-medium text-gray-700 border border-gray-200">
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">Enter</kbd>
                <span className="ml-1">Select Top</span>
              </span>
              <span className="inline-flex items-center px-2 py-1 bg-white rounded-md text-xs font-medium text-gray-700 border border-gray-200">
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">Ctrl</kbd>
                <span className="mx-1">+</span>
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">S</kbd>
                <span className="ml-1">Autofill</span>
              </span>
            </div>
          </div>
        </div>
        
        <div 
          ref={listRef}
          className="flex-grow overflow-y-auto bg-gray-50 relative"
        >
          {filteredProducts.length > 0 ? (
            <div className="h-full flex flex-col">
              {/* Single Table with Sticky Header */}
              <div className="flex-grow overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  {/* Sticky Header */}
                  <thead className="sticky top-0 z-30 bg-gray-100 shadow-md">
                    <tr>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-12">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                          checked={allFilteredSelected}
                          ref={(el) => {
                            if (el) {
                              el.indeterminate = someFilteredSelected;
                            }
                          }}
                          onChange={handleSelectAll}
                          title={allFilteredSelected ? "Deselect all" : someFilteredSelected ? "Select all filtered" : "Select all filtered"}
                        />
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-32">
                        Product Code
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider min-w-[200px]">
                        Product Name
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-24">
                        Status
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28">
                        Category
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-20">
                        UOM
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider min-w-[180px]">
                        Description
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28">
                        Branch
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28">
                        Department
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-32">
                        Negative Inv.
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-24">
                        Batch
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28">
                        Product Type
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28">
                        BOM Type
                      </th>
                    </tr>
                  </thead>
                  
                  {/* Scrollable Body */}
                  <tbody 
                    ref={tableBodyRef}
                    className="bg-white divide-y divide-gray-200 focus:outline-none"
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                  >
                    {filteredProducts.map((product, index) => (
                      <tr
                        key={product.ProductCode}
                        className={`product-row hover:bg-blue-50 transition-colors ${
                          highlightedIndex === index ? 'bg-blue-100' : ''
                        } ${selectedProducts.includes(product.ProductCode) ? 'bg-blue-50' : ''}`}
                        onClick={() => toggleProductSelection(product.ProductCode)}
                        onMouseEnter={() => setHighlightedIndex(index)}
                      >
                        <td className="px-4 py-3 whitespace-nowrap">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                            checked={selectedProducts.includes(product.ProductCode)}
                            onChange={() => {}}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 w-32">
                          {product.ProductCode}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium min-w-[200px]">
                          {product.ProductName}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm w-24">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            product.Status === 'Active' ? 'bg-green-100 text-green-800' : 
                            product.Status === 'Inactive' ? 'bg-red-100 text-red-800' : 
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {product.Status || 'N/A'}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-28">
                          {product.CategoryName || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-20">
                          {product.UOMName || 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate min-w-[180px]" title={product.Description || ''}>
                          {product.Description || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-28">
                          {product.BranchName || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-28">
                          {product.DepartmentName || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm w-32">
                          {product.AllowProductNegativeInventory !== undefined ? (
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              product.AllowProductNegativeInventory ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {product.AllowProductNegativeInventory ? 'Yes' : 'No'}
                            </span>
                          ) : 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm w-24">
                          {product.AllowBatchOnProduct !== undefined ? (
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              product.AllowBatchOnProduct ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {product.AllowBatchOnProduct ? 'Yes' : 'No'}
                            </span>
                          ) : 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-28">
                          {product.ProductType || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-28">
                          {product.ProductionBOMType || 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full py-16">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">No products found</h3>
              <p className="mt-2 text-sm text-gray-500">
                {products.length === 0 
                  ? 'No products are available in the system.' 
                  : 'No products match your search criteria. Try different keywords.'}
              </p>
            </div>
          )}
        </div>
        
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-700">
              <span className="font-medium">{selectedProducts.length}</span> product{selectedProducts.length !== 1 ? 's' : ''} selected
            </div>
            {selectedProducts.length > 0 && (
              <button
                type="button"
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                onClick={handleClearAll}
              >
                Clear All
              </button>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={handleSaveToTable}
              disabled={selectedProducts.length === 0 || isSaving}
            >
              <SaveIcon className="mr-2 h-4 w-4" />
              {isSaving ? 'Saving...' : 'Save to Table'}
            </button>
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={handleAutofill}
              disabled={selectedProducts.length === 0}
            >
              <AutofillIcon className="mr-2 h-4 w-4" />
              Autofill Table
            </button>
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              onClick={handleConfirm}
              disabled={selectedProducts.length === 0}
            >
              Add Selected
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Multi-Select Warehouse Modal Component
const WarehouseMultiSelectModal = ({ 
  open, 
  onClose, 
  warehouses, 
  onConfirm 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedWarehouses, setSelectedWarehouses] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const listRef = useRef(null);
  const searchInputRef = useRef(null);
  const tableBodyRef = useRef(null);

  // Filter warehouses based on search term
  const filteredWarehouses = useMemo(() => {
    if (!searchTerm) return warehouses;
    const term = searchTerm.toLowerCase();
    return warehouses.filter(warehouse => {
      return (
        (warehouse.LocationCode && warehouse.LocationCode.toLowerCase().includes(term)) || 
        (warehouse.LocationName && warehouse.LocationName.toLowerCase().includes(term))
      );
    });
  }, [warehouses, searchTerm]);

  // Check if all filtered warehouses are selected
  const allFilteredSelected = useMemo(() => {
    if (filteredWarehouses.length === 0) return false;
    return filteredWarehouses.every(warehouse => selectedWarehouses.includes(warehouse.LocationCode));
  }, [filteredWarehouses, selectedWarehouses]);

  // Check if some filtered warehouses are selected (for indeterminate state)
  const someFilteredSelected = useMemo(() => {
    if (filteredWarehouses.length === 0) return false;
    return filteredWarehouses.some(warehouse => selectedWarehouses.includes(warehouse.LocationCode)) && !allFilteredSelected;
  }, [filteredWarehouses, selectedWarehouses, allFilteredSelected]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (open && tableBodyRef.current && highlightedIndex >= 0) {
      const items = tableBodyRef.current.querySelectorAll('.warehouse-row');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex, open]);

  // Reset and focus when modal opens
  useEffect(() => {
    if (open) {
      setSearchTerm('');
      setSelectedWarehouses([]);
      setHighlightedIndex(-1);
      
      // Focus search input after a short delay
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }
  }, [open]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    // Handle search input keys
    if (e.target === searchInputRef.current) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex(0);
        // Move focus to the table body
        setTimeout(() => {
          if (tableBodyRef.current) {
            tableBodyRef.current.focus();
          }
        }, 0);
        return;
      }
      return;
    }

    // Handle table body navigation
    if (e.target === tableBodyRef.current) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex(prev => {
            if (prev < filteredWarehouses.length - 1) {
              return prev + 1;
            } else if (filteredWarehouses.length > 0) {
              return 0; // Loop back to first item
            }
            return prev;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex(prev => {
            if (prev > 0) {
              return prev - 1;
            } else if (filteredWarehouses.length > 0) {
              return filteredWarehouses.length - 1; // Loop to last item
            }
            return prev;
          });
          break;
        case ' ':
          e.preventDefault();
          if (highlightedIndex >= 0 && highlightedIndex < filteredWarehouses.length) {
            toggleWarehouseSelection(filteredWarehouses[highlightedIndex].LocationCode);
          }
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0 && highlightedIndex < filteredWarehouses.length) {
            // Regular Enter for selecting highlighted warehouse
            toggleWarehouseSelection(filteredWarehouses[highlightedIndex].LocationCode);
          }
          break;
        case 'Escape':
          onClose();
          break;
        default:
          break;
      }
    }
  };

  // Toggle warehouse selection
  const toggleWarehouseSelection = (warehouseCode) => {
    setSelectedWarehouses(prev => 
      prev.includes(warehouseCode)
        ? prev.filter(code => code !== warehouseCode)
        : [...prev, warehouseCode]
    );
  };

  // Handle select all filtered warehouses
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // Add all filtered warehouses to selection
      const allWarehouseCodes = filteredWarehouses.map(w => w.LocationCode);
      setSelectedWarehouses(prev => [...new Set([...prev, ...allWarehouseCodes])]);
    } else {
      // Remove all filtered warehouses from selection
      const filteredWarehouseCodes = filteredWarehouses.map(w => w.LocationCode);
      setSelectedWarehouses(prev => prev.filter(code => !filteredWarehouseCodes.includes(code)));
    }
  };

  // Handle clear all selection
  const handleClearAll = () => {
    setSelectedWarehouses([]);
  };

  // Handle confirm selection
  const handleConfirm = () => {
    onConfirm(selectedWarehouses);
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl h-[80vh] flex flex-col border border-gray-200">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold text-gray-800">Select Multiple Warehouses</h3>
            <button 
              onClick={onClose} 
              className="text-gray-500 hover:text-gray-700 transition-colors p-1 rounded-full hover:bg-gray-200"
            >
              <CloseIcon size={24} />
            </button>
          </div>
        </div>
        
        <div className="p-5 border-b border-gray-200 bg-white">
          <div className="relative">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by Warehouse Code or Name..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base shadow-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <div className="absolute right-3 top-3 text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          <div className="mt-3 text-sm text-gray-600 bg-blue-50 p-3 rounded-lg border border-blue-100">
            <div className="font-medium text-blue-800 mb-1">Keyboard Shortcuts:</div>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center px-2 py-1 bg-white rounded-md text-xs font-medium text-gray-700 border border-gray-200">
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">↑</kbd>
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">↓</kbd>
                <span className="ml-1">Navigate</span>
              </span>
              <span className="inline-flex items-center px-2 py-1 bg-white rounded-md text-xs font-medium text-gray-700 border border-gray-200">
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">Space</kbd>
                <span className="ml-1">Select</span>
              </span>
              <span className="inline-flex items-center px-2 py-1 bg-white rounded-md text-xs font-medium text-gray-700 border border-gray-200">
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">Enter</kbd>
                <span className="ml-1">Select</span>
              </span>
            </div>
          </div>
        </div>
        
        <div 
          ref={listRef}
          className="flex-grow overflow-y-auto bg-gray-50 relative"
        >
          {filteredWarehouses.length > 0 ? (
            <div className="h-full flex flex-col">
              {/* Single Table with Sticky Header */}
              <div className="flex-grow overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  {/* Sticky Header */}
                  <thead className="sticky top-0 z-30 bg-gray-100 shadow-md">
                    <tr>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-12">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                          checked={allFilteredSelected}
                          ref={(el) => {
                            if (el) {
                              el.indeterminate = someFilteredSelected;
                            }
                          }}
                          onChange={handleSelectAll}
                          title={allFilteredSelected ? "Deselect all" : someFilteredSelected ? "Select all filtered" : "Select all filtered"}
                        />
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-32">
                        Warehouse Code
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider min-w-[200px]">
                        Warehouse Name
                      </th>
                    </tr>
                  </thead>
                  
                  {/* Scrollable Body */}
                  <tbody 
                    ref={tableBodyRef}
                    className="bg-white divide-y divide-gray-200 focus:outline-none"
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                  >
                    {filteredWarehouses.map((warehouse, index) => (
                      <tr
                        key={warehouse.LocationCode}
                        className={`warehouse-row hover:bg-blue-50 transition-colors ${
                          highlightedIndex === index ? 'bg-blue-100' : ''
                        } ${selectedWarehouses.includes(warehouse.LocationCode) ? 'bg-blue-50' : ''}`}
                        onClick={() => toggleWarehouseSelection(warehouse.LocationCode)}
                        onMouseEnter={() => setHighlightedIndex(index)}
                      >
                        <td className="px-4 py-3 whitespace-nowrap">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                            checked={selectedWarehouses.includes(warehouse.LocationCode)}
                            onChange={() => {}}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 w-32">
                          {warehouse.LocationCode}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium min-w-[200px]">
                          {warehouse.LocationName}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full py-16">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">No warehouses found</h3>
              <p className="mt-2 text-sm text-gray-500">
                {warehouses.length === 0 
                  ? 'No warehouses are available in the system.' 
                  : 'No warehouses match your search criteria. Try different keywords.'}
              </p>
            </div>
          )}
        </div>
        
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-700">
              <span className="font-medium">{selectedWarehouses.length}</span> warehouse{selectedWarehouses.length !== 1 ? 's' : ''} selected
            </div>
            {selectedWarehouses.length > 0 && (
              <button
                type="button"
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                onClick={handleClearAll}
              >
                Clear All
              </button>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              onClick={handleConfirm}
              disabled={selectedWarehouses.length === 0}
            >
              Add Selected
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const BatchTableModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  initialBatches = [], 
  productName = "", 
  totalQuantity = 0,
  acceptedQuantity = 0 
}) => {
  const [batches, setBatches] = useState([
    { batchNumber: "", expiryDate: "", quantity: "" }
  ]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
  if (isOpen) {
    console.log('BatchTableModal opened with initialBatches:', initialBatches);
    console.log('BatchTableModal productName:', productName);
    console.log('BatchTableModal acceptedQuantity:', acceptedQuantity);
    
    if (initialBatches.length > 0) {
      // Format the expiry dates to YYYY-MM-DD format for the input[type="date"]
      const formattedBatches = initialBatches.map(batch => ({
        ...batch,
        expiryDate: batch.expiryDate ? new Date(batch.expiryDate).toISOString().split('T')[0] : ""
      }));
      console.log('Formatted batches:', formattedBatches);
      setBatches(formattedBatches);
    } else {
      console.log('No initial batches, setting empty batch');
      setBatches([{ batchNumber: "", expiryDate: "", quantity: "" }]);
    }
    setErrors({});
    setIsSubmitting(false);
  }
}, [isOpen, initialBatches, productName, acceptedQuantity]);

  // Calculate remaining quantity
  const remainingQuantity = acceptedQuantity - batches.reduce(
    (sum, batch) => sum + (parseFloat(batch.quantity) || 0), 0
  );

  // Handle batch field changes
  const handleBatchChange = (index, field, value) => {
    const newBatches = [...batches];
    newBatches[index][field] = value;
    
    // Clear errors for this field
    if (errors[`${field}_${index}`]) {
      const newErrors = { ...errors };
      delete newErrors[`${field}_${index}`];
      setErrors(newErrors);
    }
    
    // Auto-generate new row if quantity is entered and it's less than remaining
    if (field === 'quantity' && value && index === batches.length - 1) {
      const enteredQty = parseFloat(value) || 0;
      const currentTotal = batches.reduce((sum, batch, i) => 
        i === index ? sum + enteredQty : sum + (parseFloat(batch.quantity) || 0), 0);
      
      // If there's still remaining quantity after this entry, add a new row
      if (currentTotal < acceptedQuantity) {
        // Add a new empty row
        newBatches.push({ batchNumber: "", expiryDate: "", quantity: "" });
      }
    }
    
    setBatches(newBatches);
  };

  // Remove a batch row
  const removeBatchRow = (index) => {
    if (batches.length > 1) {
      const newBatches = [...batches];
      newBatches.splice(index, 1);
      setBatches(newBatches);
    }
  };

  // Auto-fill quantity to cover remaining
  const autoFillQuantity = (index) => {
    const newBatches = [...batches];
    newBatches[index].quantity = remainingQuantity.toString();
    
    // Remove any extra empty rows if we've covered the full quantity
    if (remainingQuantity > 0) {
      // Keep only this row if it covers the full remaining quantity
      setBatches([newBatches[index]]);
    } else {
      setBatches(newBatches);
    }
  };

  // Validate all batches
  const validateBatches = () => {
    const newErrors = {};
    let isValid = true;

    // Check if we have at least one batch
    if (batches.length === 0) {
      newErrors.general = "At least one batch is required";
      isValid = false;
    }

    batches.forEach((batch, index) => {
      // Validate batch number
      if (!batch.batchNumber || batch.batchNumber.trim() === '') {
        newErrors[`batchNumber_${index}`] = "Batch number is required";
        isValid = false;
      }

      // Validate expiry date
      if (!batch.expiryDate) {
        newErrors[`expiryDate_${index}`] = "Expiry date is required";
        isValid = false;
      }

      // Validate quantity
      if (!batch.quantity || batch.quantity.trim() === '' || parseFloat(batch.quantity) <= 0) {
        newErrors[`quantity_${index}`] = "Quantity must be greater than 0";
        isValid = false;
      }
    });

    // Check if total batch quantity equals accepted quantity
    const totalBatchQty = batches.reduce(
      (sum, batch) => sum + (parseFloat(batch.quantity) || 0), 0
    );
    
    if (Math.abs(totalBatchQty - acceptedQuantity) > 0.001) {
      newErrors.total = `Total batch quantity (${totalBatchQty.toFixed(2)}) must equal accepted quantity (${acceptedQuantity})`;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSave = async () => {
  setIsSubmitting(true);
  
  try {
    if (validateBatches()) {
      // Filter out any incomplete batches (only include batches with all required fields)
      const validBatches = batches.filter(batch => 
        batch.batchNumber && 
        batch.batchNumber.trim() !== '' && 
        batch.expiryDate && 
        batch.quantity && 
        parseFloat(batch.quantity) > 0
      );
      
      // Format batches for backend
      const formattedBatches = validBatches.map(batch => ({
        batchNumber: batch.batchNumber.trim(),
        expiryDate: batch.expiryDate,
        quantity: batch.quantity.toString()
      }));
      
      await onSave(formattedBatches);
      onClose();
    }
  } catch (error) {
    console.error('Error saving batches:', error);
    setErrors({
      ...errors,
      general: error.message || "Failed to save batches"
    });
  } finally {
    setIsSubmitting(false);
  }
};
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl p-0 max-h-[90vh] flex flex-col border border-gray-300">
        {/* Header */}
        <div className="bg-slate-200 px-6 py-4 rounded-t-lg">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold text-black">Batch Details</h3>
            <button 
              onClick={onClose} 
              className="text-gray-900 hover:text-black transition-colors"
            >
              <CloseIcon size={24} />
            </button>
          </div>
        </div>
        
        {/* Product Info Bar */}
        <div className="bg-slate-100 px-6 py-3 border-b border-slate-200">
          <div className="flex items-center space-x-8">
            <div className="flex items-center">
              <span className="text-sm font-medium text-slate-600 mr-2">Product:</span>
              <span className="text-sm font-semibold text-slate-800">{productName || "N/A"}</span>
            </div>
            <div className="flex items-center">
              <span className="text-sm font-medium text-slate-600 mr-2">Total Quantity:</span>
              <span className="text-sm font-semibold text-slate-800">{acceptedQuantity}</span>
            </div>
            <div className="flex items-center">
              <span className="text-sm font-medium text-slate-600 mr-2">Remaining:</span>
              <span className={`text-sm font-semibold ${remainingQuantity > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {remainingQuantity.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
        
        {/* Instructions */}
        <div className="px-6 py-3 bg-blue-50 border-b border-blue-100">
          <p className="text-sm text-blue-700">
            <span className="font-medium">Instructions:</span> Enter batch details for each batch. 
            New rows will be automatically added when you enter a quantity less than the total required. 
            Use the "Fill" button to automatically fill a row with the remaining quantity.
          </p>
        </div>
        
        {/* Error Messages */}
        {(errors.general || errors.total) && (
          <div className="mx-6 mt-4 space-y-2">
            {errors.general && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{errors.general}</p>
              </div>
            )}
            {errors.total && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{errors.total}</p>
              </div>
            )}
          </div>
        )}
        
        {/* Batch Table */}
        <div className="flex-grow overflow-hidden flex flex-col p-6">
          <div className="overflow-y-auto flex-grow">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-100 sticky top-0 z-10 shadow-sm">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider w-12">#</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">Batch Number *</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">Expiry Date *</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider w-32">Quantity *</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {batches.map((batch, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50 hover:bg-slate-100'}>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-slate-900">
                      {index + 1}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                     <input
  type="text"
  value={batch.batchNumber}
  onChange={(e) => handleBatchChange(index, 'batchNumber', e.target.value.toUpperCase())}
  className={`w-full border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500 uppercase text-sm ${errors[`batchNumber_${index}`] ? 'border-red-500' : ''}`}
  placeholder="Enter batch number"
/>
                      {errors[`batchNumber_${index}`] && (
                        <p className="text-red-500 text-xs mt-1">{errors[`batchNumber_${index}`]}</p>
                      )}
                    </td>
                     <td className="px-4 py-3 whitespace-nowrap">
    <input
      type="date"
      value={batch.expiryDate}
      onChange={(e) => handleBatchChange(index, 'expiryDate', e.target.value)}
      className={`w-full border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500 text-sm ${errors[`expiryDate_${index}`] ? 'border-red-500' : ''}`}
    />
    {errors[`expiryDate_${index}`] && (
      <p className="text-red-500 text-xs mt-1">{errors[`expiryDate_${index}`]}</p>
    )}
  </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={batch.quantity}
                          onChange={(e) => handleBatchChange(index, 'quantity', e.target.value)}
                          className={`w-full border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500 text-sm ${errors[`quantity_${index}`] ? 'border-red-500' : ''}`}
                          placeholder="0.00"
                        />
                        <button
                          type="button"
                          onClick={() => autoFillQuantity(index)}
                          className="px-2 py-1 bg-blue-100 text-blue-700 rounded-md text-xs hover:bg-blue-200"
                          title="Auto-fill with remaining quantity"
                        >
                          Fill
                        </button>
                      </div>
                      {errors[`quantity_${index}`] && (
                        <p className="text-red-500 text-xs mt-1">{errors[`quantity_${index}`]}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex space-x-1">
                        <button
                          type="button"
                          onClick={() => removeBatchRow(index)}
                          disabled={batches.length <= 1}
                          className={`p-1 rounded-md ${batches.length <= 1 ? 'text-gray-300 cursor-not-allowed' : 'text-red-600 hover:bg-red-100'}`}
                          title="Remove batch"
                        >
                          <TrashIcon size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Footer */}
        <div className="bg-slate-50 px-6 py-4 border-t border-slate-200 rounded-b-lg">
          <div className="flex justify-between items-center">
            <div className="text-sm text-slate-600">
              Total batches: <span className="font-medium">{batches.length}</span> | 
              Remaining quantity: <span className={`font-medium ${remainingQuantity > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {remainingQuantity.toFixed(2)}
              </span>
            </div>
            <div className="flex space-x-3">
              <button
                type="button"
                className="px-4 py-2 border border-slate-300 rounded-md text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 transition-colors"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-white bg-slate-700 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 transition-colors disabled:opacity-50"
                onClick={handleSave}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : 'Save Batch Details'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SerialModal =  ({ isOpen, onClose, onSave, count = 0, initialSerialNumbers = [], productName = "", locationCode = "" }) => {
  const [serialNumbers, setSerialNumbers] = useState(
    Array.from({ length: count }, (_, i) => initialSerialNumbers[i] || "")
  );
  const inputRefs = useRef([]);

 useEffect(() => {
  if (isOpen) {
    console.log('SerialModal opened with initialSerialNumbers:', initialSerialNumbers);
    console.log('SerialModal count:', count);
    console.log('SerialModal productName:', productName);
    console.log('SerialModal locationCode:', locationCode);
    
    if (initialSerialNumbers.length > 0) {
      console.log('Setting initial serial numbers:', initialSerialNumbers);
      setSerialNumbers(initialSerialNumbers);
    } else {
      console.log('No initial serial numbers, setting empty array');
      setSerialNumbers(Array.from({ length: count }, () => ""));
    }
    
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }
}, [isOpen, count, initialSerialNumbers, productName, locationCode]);

  const handleSerialChange = (index, value) => {
    const newSerialNumbers = [...serialNumbers];
    newSerialNumbers[index] = value;
    setSerialNumbers(newSerialNumbers);
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      if (index < count - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleSave = () => {
    if (serialNumbers.every(sn => sn.trim() !== "")) {
      onSave(serialNumbers.map(sn => sn.trim()));
      onClose();
    } else {
      alert("All serial numbers are required");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl p-0 max-h-[90vh] flex flex-col border border-gray-300">
        {/* Header */}
        <div className="bg-slate-200 px-4 py-2 rounded-t-lg">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-bold text-black">Serial Number Entry</h3>
            <button 
              onClick={onClose} 
              className="text-gray-900 hover:text-black transition-colors"
            >
              <CloseIcon size={20} />
            </button>
          </div>
        </div>
        
        {/* Product Info Bar */}
        <div className="bg-slate-100 px-4 py-2 border-b border-slate-200">
          <div className="flex items-center space-x-6">
            <div className="flex items-center">
              <span className="text-xs font-medium text-slate-600 mr-1">Product:</span>
              <span className="text-xs font-semibold text-slate-800">{productName || "N/A"}</span>
            </div>
            <div className="flex items-center">
              <span className="text-xs font-medium text-slate-600 mr-1">Location:</span>
              <span className="text-xs font-semibold text-slate-800">{locationCode || "N/A"}</span>
            </div>
            <div className="flex items-center">
              <span className="text-xs font-medium text-slate-600 mr-1">Quantity:</span>
              <span className="text-xs font-semibold text-slate-800">{count}</span>
            </div>
          </div>
        </div>
        
        {/* Instructions */}
        <div className="px-4 py-2 bg-slate-50 border-b border-slate-200">
          <p className="text-xs text-slate-700">
            Enter {count} unique serial number{count !== 1 ? 's' : ''}. Press Enter or Tab to move to the next field.
          </p>
        </div>
        
        {/* Serial Numbers Table */}
        <div className="flex-grow overflow-hidden flex flex-col">
          <div className="overflow-y-auto flex-grow">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-100 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-700 uppercase tracking-wider w-10">#</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">Serial Number</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">Product</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">Location</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-700 uppercase tracking-wider w-10">Qty</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {Array.from({ length: count }).map((_, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50 hover:bg-slate-100'}>
                    <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-slate-900">
                      {index + 1}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <input
                        ref={el => inputRefs.current[index] = el}
                        type="text"
                        value={serialNumbers[index]}
                        onChange={(e) => handleSerialChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        className="w-full border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500 uppercase text-xs"
                        placeholder={`Serial ${index + 1}`}
                      />
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-900">
                      {productName || "N/A"}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">
                      {locationCode || "N/A"}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600 text-center">
                      1
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Footer */}
        <div className="bg-slate-50 px-4 py-2 border-t border-slate-200 rounded-b-lg">
          <div className="flex justify-between items-center">
            <div className="text-xs text-slate-600">
              {serialNumbers.filter(sn => sn.trim() !== "").length} of {count} serial numbers entered
            </div>
            <div className="flex space-x-2">
              <button
                className="px-3 py-1 border border-slate-300 rounded text-xs text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-1 focus:ring-slate-500 transition-colors"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                className="px-3 py-1 border border-transparent rounded text-xs text-white bg-slate-700 hover:bg-slate-800 focus:outline-none focus:ring-1 focus:ring-slate-500 transition-colors"
                onClick={handleSave}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StockInFormModal = ({ open, onClose, onSubmit, initialData, isEdit }) => {
  const [header, setHeader] = useState(initialHeader);
  const [items, setItems] = useState([{ ...initialItem }]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dropdownData, setDropdownData] = useState({
    users: [],
    products: [],
    locations: [],
    uoms: [],
    nextStockInNumber: '',
    branches: [],
    nextItemNumber: ''
  });
  
  const [currentUser, setCurrentUser] = useState(null);
  const [isUserValid, setIsUserValid] = useState(false);
  const [authError, setAuthError] = useState(null);
  
  const [isMaximized, setIsMaximized] = useState(false);
  
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [screenConfig, setScreenConfig] = useState(null);
  const [showDepartmentField, setShowDepartmentField] = useState(false);
  const [showDepartmentItemField, setShowDepartmentItemField] = useState(false);

  const [showBatchModal, setShowBatchModal] = useState(false);
const [showSerialModal, setShowSerialModal] = useState(false);
const [currentBatchItem, setCurrentBatchItem] = useState(null);
const [currentSerialItem, setCurrentSerialItem] = useState(null);
  
  const [showMultiSelectModal, setShowMultiSelectModal] = useState(false);
  const [showWarehouseMultiSelectModal, setShowWarehouseMultiSelectModal] = useState(false);
  const [savedItems, setSavedItems] = useState([]); // State for saved items from table
  
  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setIsUserValid(false);
        setAuthError('No token found. Please log in again.');
        return false;
      }
      const userRes = await fetch(`${API_URL}/current-user`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const userData = await userRes.json();
      
      if (userData.user && userData.user.UserID) {
        setCurrentUser(userData.user);
        setIsUserValid(true);
        setAuthError(null);
        console.log('Current user fetched:', userData);
        return true;
      } else {
        console.error('Invalid user data received:', userData);
        setIsUserValid(false);
        setAuthError('User information not available. Please log in again.');
        return false;
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
      setIsUserValid(false);
      setAuthError('Error fetching user information. Please refresh the page.');
      return false;
    }
  };
  

  
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };
  
  // ERP Command: Save popup items to table
  const executeERPCommand = async (command, data) => {
    try {
      const response = await fetch(`${API_URL}/erp/execute-command`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          command,
          data
        })
      });
      
      if (!response.ok) {
        throw new Error(`ERP command failed: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error executing ERP command ${command}:`, error);
      throw error;
    }
  };
  
  // Save selected products to table
  const savePopupItemsToTable = async (selectedProductCodes) => {
    const productsToSave = selectedProductCodes.map(productCode => {
      const product = dropdownData.products.find(p => p.ProductCode === productCode);
      return {
        productCode: product.ProductCode,
        productName: product.ProductName,
        uom: product.UOMName || product.UOMID || '',
        savedDate: new Date().toISOString()
      };
    });
    
    return await executeERPCommand(ERP_COMMANDS.SAVE_POPUP_ITEMS, {
      stockInId: header.stockInId,
      items: productsToSave
    });
  };
  
  // Load saved items from table
  const loadSavedItemsFromTable = async () => {
    try {
      const result = await executeERPCommand(ERP_COMMANDS.LOAD_SAVED_ITEMS, {
        stockInId: header.stockInId
      });
      
      if (result && result.items && result.items.length > 0) {
        setSavedItems(result.items);
        
        // Convert saved items to form items
        const formItems = result.items.map(savedItem => ({
          ...initialItem,
          itemCode: savedItem.productCode,
          itemName: savedItem.productName,
          uom: savedItem.uom,
          savedToTable: true
        }));
        
        setItems(formItems);
      }
    } catch (error) {
      console.error('Error loading saved items:', error);
    }
  };
  
  // Delete saved items from table
  const deleteSavedItemsFromTable = async () => {
    try {
      await executeERPCommand(ERP_COMMANDS.DELETE_SAVED_ITEMS, {
        stockInId: header.stockInId
      });
      setSavedItems([]);
    } catch (error) {
      console.error('Error deleting saved items:', error);
    }
  };
  
  // Autofill selected products into the table
const autofillSelectedProducts = (selectedProductCodes) => {
  if (selectedProductCodes.length === 0) return;
  
  try {
    const newItems = selectedProductCodes.map(productCode => {
      const product = dropdownData.products.find(p => p.ProductCode === productCode);
      if (!product) return { ...initialItem };
      
      // Parse AllowProductTracking as integer
      const trackingType = parseInt(product.AllowProductTracking) || 0;
      
      return {
        ...initialItem,
        itemCode: product.ProductCode,
        itemName: product.ProductName,
        uom: product.UOMName || product.UOMID || '',
        allowBatch: product.AllowBatchOnProduct || false,
        productTrackingType: trackingType,
        department_id: showDepartmentItemField ? "" : undefined,
      };
    });
    
    setItems(prevItems => [...prevItems, ...newItems]);
    
    return { success: true, count: selectedProductCodes.length };
  } catch (error) {
    console.error('Error in autofillSelectedProducts:', error);
    return { success: false, error: error.message };
  }
};
  
const handleMultiSelectProducts = (selectedProductCodes) => {
  if (selectedProductCodes.length === 0) return;
  
  const newItems = selectedProductCodes.map(productCode => {
    const product = dropdownData.products.find(p => p.ProductCode === productCode);
    if (!product) return { ...initialItem };
    
    // Parse AllowProductTracking as integer
    const trackingType = parseInt(product.AllowProductTracking) || 0;
    
    return {
      ...initialItem,
      itemCode: product.ProductCode,
      itemName: product.ProductName,
      uom: product.UOMName || product.UOMID || '',
      allowBatch: product.AllowBatchOnProduct || false,
      productTrackingType: trackingType,
      department_id: showDepartmentItemField ? "" : undefined,
    };
  });
  
  setItems(prevItems => [...prevItems, ...newItems]);
};

// Handle multi-select warehouses
const handleMultiSelectWarehouses = (selectedWarehouseCodes) => {
  if (selectedWarehouseCodes.length === 0) return;
  
  // For now, just use the first selected warehouse
  // You can modify this logic based on your requirements
  const firstWarehouseCode = selectedWarehouseCodes[0];
  
  // Update all items to use the selected warehouse
  setItems(prevItems => 
    prevItems.map(item => ({
      ...item,
      locationCode: firstWarehouseCode
    }))
  );
};
  
  const addItem = useCallback(() => {
    setItems(prevItems => [...prevItems, { 
      ...initialItem,
      department_id: showDepartmentItemField ? "" : undefined,
    }]);
  }, [showDepartmentItemField]);

  const removeItem = useCallback((idx) => {
    setItems(prevItems => {
      if (prevItems.length > 1) {
        return prevItems.filter((_, i) => i !== idx);
      }
      return prevItems;
    });
  }, []);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      // Check for Ctrl+P - Multi-select products
      if (e.altKey && e.key === 'p' || e.key === 'P') {
        e.preventDefault();
        setShowMultiSelectModal(true);
        return;
      }
      
      // Check for Ctrl+W - Multi-select warehouses
      if (e.altKey && e.key === 'w') {
        e.preventDefault();
        setShowWarehouseMultiSelectModal(true);
        return;
      }
      
      // Check for Ctrl+F2 - Add new item
      if (e.ctrlKey && e.key === 'F2') {
        e.preventDefault();
        addItem();
        return;
      }
      
      // Check for Ctrl+F3 - Remove last item
      if (e.ctrlKey && e.key === 'F3') {
        e.preventDefault();
        if (items.length > 1) {
          removeItem(items.length - 1);
        }
        return;
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, addItem, removeItem, items.length]);
  
  const calculateItemValue = (item) => {
    const quantity = parseFloat(item.quantityAdded) || 0;
    const rate = parseFloat(item.rate) || 0;
    const value = quantity * rate;
    return value.toFixed(2);
  };
  
  const summary = useMemo(() => {
    let totalQuantity = 0;
    let totalValue = 0;
    
    items.forEach(item => {
      const quantity = parseFloat(item.quantityAdded) || 0;
      const value = parseFloat(item.value) || 0;
      totalQuantity += quantity;
      totalValue += value;
    });
    
    return {
      totalQuantity: totalQuantity.toString(),
      totalValue: totalValue.toFixed(2)
    };
  }, [items]);
  
  useEffect(() => {
    setHeader(prev => ({
      ...prev,
      totalQuantity: summary.totalQuantity
    }));
  }, [summary]);
  
  useEffect(() => {
    if (open) {
      setIsMaximized(false);
      fetchCurrentUser();
      // fetchScreenConfig();
      
      const getUserFromToken = () => {
        try {
          const token = localStorage.getItem('authToken');
          if (!token) {
            throw new Error('No authentication token found');
          }
          
          console.log('Found authToken:', token);
          
          const parts = token.split('.');
          if (parts.length !== 3) {
            throw new Error('Invalid token format');
          }
          
          const payload = JSON.parse(atob(parts[1]));
          console.log('Decoded token payload:', payload);
          
          return {
            UserID: payload.userId,
            UserName: payload.email
          };
        } catch (error) {
          console.error('Error extracting user from token:', error);
          return null;
        }
      };
      
      const initializeForm = async () => {
        try {
          let currentUser = getUserFromToken();
          
          if (!currentUser || !currentUser.UserID) {
            console.error('Could not extract user information from token');
            alert('You must be logged in to create stock in records');
            onClose();
            return;
          }

          // Fetch dropdown data and get the result
        const dropdownDataResult = await fetchDropdownData();
        console.log('Dropdown data result:', dropdownDataResult); // Add this log
        
          
          try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_URL}/users/${currentUser.UserID}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (response.ok) {
              const userDetails = await response.json();
              console.log('Fetched user details:', userDetails);
              
              currentUser.UserName = userDetails.Username || userDetails.FullName || userDetails.Email;
            }
          } catch (error) {
            console.error('Error fetching user details:', error);
          }
          
          console.log('User authenticated:', currentUser);
          
          fetchDropdownData();
          
          // Load saved items from table for new records
          if (!isEdit) {
            loadSavedItemsFromTable();
          }
          
          if (isEdit && initialData) {
            console.log('Initial data received:', initialData);
            console.log('BranchCode from initialData:', initialData.BranchCode);
            
            setHeader({
              stockInId: initialData.StockInID || '',
              stockInDate: initialData.StockInDate ? new Date(initialData.StockInDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
              addedByUserId: initialData.AddedByUserID?.toString() || '',
              addedByUserName: initialData.AddedByUserName || '',
              branchCode: initialData.BranchCode || initialData.branchcode || '',
              note: initialData.Note || '',
              status: initialData.Status ? '1' : '0',
              inventoryFlag: initialData.InventoryFlag ? 'yes' : 'no',
              totalQuantity: initialData.TotalQuantity?.toString() || '0',
              department_id: initialData.DepartmentID?.toString() || "",
            });
            
            
// In the useEffect that initializes the form, update the API calls
if (isEdit && initialData && initialData.Items && initialData.Items.length > 0) {
  console.log('Processing initial items for edit mode:', initialData.Items);
  
  // First, map the basic item data
  const mappedItems = initialData.Items.map((item) => {
    console.log('Mapping item:', item);
    
    // Parse ProductTrackingType as integer
    const trackingType = parseInt(item.ProductTrackingType) || 0;
    console.log(`Item ${item.ItemName} tracking type:`, trackingType);
    
    return {
      ...initialItem,
      itemCode: item.ItemCode || '',
      itemName: item.ItemName || '',
      uom: item.UOM || '',
      quantityAdded: item.QuantityAdded?.toString() || '',
      rate: item.Rate?.toString() || '',
      value: item.Value?.toString() || '0',
      locationCode: item.LocationCode || '',
      department_id: item.DepartmentID?.toString() || "",
      allowBatch: trackingType === 1,
      savedToTable: false,
      productTrackingType: trackingType,
      batches: [],
      serialNumbers: [],
      StockInItemID: item.StockInItemID,
    };
  });
  
  console.log('Mapped items before fetching tracking data:', mappedItems);
  
  // Now fetch tracking data for items that need it
  const itemsWithTrackingData = await Promise.all(
    mappedItems.map(async (item) => {
      console.log(`Processing item for tracking data:`, item);
      
      // Only for existing items (with StockInItemID) and with tracking type 1 or 2
      if (item.StockInItemID && (item.productTrackingType === 1 || item.productTrackingType === 2) && item.itemCode && item.locationCode) {
        console.log(`Fetching tracking data for item ${item.itemName} with tracking type ${item.productTrackingType}`);
        
        try {
          // Make sure we have the correct stockInId from initialData
          const stockInId = initialData.StockInID || header.stockInId;
          console.log('Using stockInId:', stockInId);
          
          if (item.productTrackingType === 1) {
            // Fetch batch data
            const batchData = await fetchBatchData(
              stockInId,
              item.StockInItemID,
              item.itemCode,
              item.locationCode
            );
            console.log(`Batch data for ${item.itemName}:`, batchData);
            
            // Map the batch data to our format
            const batches = batchData.map(batch => ({
              batchNumber: batch.BatchNumber,
              expiryDate: batch.ExpiryDate ? new Date(batch.ExpiryDate).toISOString().split('T')[0] : "",
              quantity: batch.Quantity.toString()
            }));
            
            console.log(`Mapped batches for ${item.itemName}:`, batches);
            return { ...item, batches };
          } else if (item.productTrackingType === 2) {
            // Fetch serial data
            const serialData = await fetchSerialData(
              stockInId,
              item.StockInItemID,
              item.itemCode,
              item.locationCode
            );
            console.log(`Serial data for ${item.itemName}:`, serialData);
            
            const serialNumbers = serialData.map(serial => serial.SerialNumber);
            console.log(`Mapped serial numbers for ${item.itemName}:`, serialNumbers);
            
            return { ...item, serialNumbers };
          }
        } catch (error) {
          console.error('Error fetching tracking data for item:', item.itemName, error);
          // Return the item without tracking data (will have empty arrays)
          return item;
        }
      } else {
        console.log(`Skipping tracking data fetch for item ${item.itemName}. Conditions:`, {
          hasStockInItemID: !!item.StockInItemID,
          hasTrackingType: item.productTrackingType === 1 || item.productTrackingType === 2,
          hasItemCode: !!item.itemCode,
          hasLocationCode: !!item.locationCode
        });
      }
      return item;
    })
  );
  
  console.log('Final items with tracking data:', itemsWithTrackingData);
  setItems(itemsWithTrackingData);
} else {
  console.log('No initial items or not in edit mode, setting empty items');
  setItems([{ ...initialItem }]);
}
          } else {
            setHeader({
              ...initialHeader,
               stockInId: dropdownDataResult?.nextStockInNumber || 'STKIN-1', // Use the fetched value
              addedByUserId: currentUser.UserID.toString(),
              addedByUserName: currentUser.UserName || ''
            });
            
            // If no saved items, initialize with one empty item
            if (savedItems.length === 0) {
              setItems([{ ...initialItem }]);
            }
          }
          
          setErrors({});
          setIsSubmitting(false);
        } catch (error) {
          console.error('Error initializing form:', error);
          alert('Error initializing form. Please try again.');
          onClose();
        }
      };
      
      initializeForm();
    }
  }, [open, initialData, isEdit, onClose, savedItems.length]);
  
  useEffect(() => {
    console.log('Header state updated:', header);
  }, [header]);
  
  // Add these functions to your StockInFormModal component

const fetchUsers = async () => {
  try {
    const response = await fetch(`${API_URL}/users`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    if (!response.ok) throw new Error("Failed to fetch users");
    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error("Error fetching users:", error);
    return [];
  }
};

const fetchProducts = async () => {
  try {
    const response = await fetch(`${API_URL}/products`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    if (!response.ok) throw new Error("Failed to fetch products");
    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error("Error fetching products:", error);
    return [];
  }
};

const fetchLocations = async () => {
  try {
    const response = await fetch(`${API_URL}/locations`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    if (!response.ok) throw new Error("Failed to fetch locations");
    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error("Error fetching locations:", error);
    return [];
  }
};

const fetchBranches = async () => {
  try {
    const response = await fetch(`${API_URL}/branches`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    if (!response.ok) throw new Error("Failed to fetch branches");
    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error("Error fetching branches:", error);
    return [];
  }
};

const fetchNextStockInNumber = async () => {
  try {
    const response = await fetch(`${API_URL}/stockin/next-number`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    if (!response.ok) throw new Error("Failed to fetch next StockIn number");
    const data = await response.json();
    console.log('Fetched next StockIn number:', data.nextStockInNumber); // Add this log
    return data.nextStockInNumber || "";
  } catch (error) {
    console.error("Error fetching next StockIn number:", error);
    return "";
  }
};

const fetchNextItemNumber = async () => {
  try {
    const response = await fetch(`${API_URL}/stockin/next-item-number`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    if (!response.ok) throw new Error("Failed to fetch next item number");
    const data = await response.json();
    return data.nextItemNumber || "";
  } catch (error) {
    console.error("Error fetching next item number:", error);
    return "";
  }
};

const fetchDepartments = async () => {
  try {
    const response = await fetch(`${API_URL}/departments`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    if (!response.ok) throw new Error("Failed to fetch departments");
    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error("Error fetching departments:", error);
    return [];
  }
};

const fetchScreenConfig = async () => {
  try {
    const response = await fetch(`${API_URL}/screens`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    const data = await response.json();
    
    // Find the StockIn screen configuration
    const stockInScreen = data.screens.find(screen => 
      screen.ScreenName.toLowerCase() === 'StockIn'.toLowerCase()
    );
    
    return stockInScreen || null;
  } catch (error) {
    console.error("Error fetching screen configuration:", error);
    return null;
  }
};

const fetchCompanySettings = async () => {
  try {
    const response = await fetch(`${API_URL}/company/settings`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    if (!response.ok) throw new Error("Failed to fetch company settings");
    const data = await response.json();
    return data || null;
  } catch (error) {
    console.error("Error fetching company settings:", error);
    return null;
  }
};

const fetchUoms = async () => {
  try {
    const response = await fetch(`${API_URL}/uoms`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    if (!response.ok) throw new Error("Failed to fetch UOMs");
    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error("Error fetching UOMs:", error);
    return [];
  }
};


const fetchDropdownData = async () => {
  try {
    setLoading(true);
    
    // Fetch all data in parallel
    const [
      users, 
      products, 
      locations, 
      branches, 
      nextStockInNumber, 
      nextItemNumber,
      departments,
      screenConfig,
      companySettings,
      uoms
    ] = await Promise.all([
      fetchUsers(),
      fetchProducts(),
      fetchLocations(),
      fetchBranches(),
      fetchNextStockInNumber(),
      fetchNextItemNumber(),
      fetchDepartments(),
      fetchScreenConfig(),
      fetchCompanySettings(),
      fetchUoms()
    ]);
    
    // Update the dropdown data state
    setDropdownData({
      users,
      products,
      locations,
      branches,
      nextStockInNumber,
      nextItemNumber,
      uoms
    });
    
    // Set departments and screen config separately
    setDepartments(departments);
    setScreenConfig(screenConfig);
    
    // Set company settings if available
    if (companySettings) {
      // You might need to store this in state if you use it elsewhere
      // For example: setCompanySettings(companySettings);
    }
    
    // Show department field if ShowOnHeader is true
    const shouldShowDepartment = screenConfig?.ShowOnHeader === true;
    setShowDepartmentField(shouldShowDepartment);
    
    // Show department item field if ShowOnItem is true
    const shouldShowDepartmentItem = screenConfig?.ShowOnItem === true;
    setShowDepartmentItemField(shouldShowDepartmentItem);
    
    // Return the data for immediate use
    return {
      users,
      products,
      locations,
      branches,
      nextStockInNumber,
      nextItemNumber,
      uoms,
      departments,
      screenConfig,
      companySettings
    };
  } catch (error) {
    console.error("Error fetching dropdown data:", error);
    alert(`Error loading form data: ${error.message}`);
    return null;
  } finally {
    setLoading(false);
  }
};
  
  const handleHeaderChange = (e) => {
    const { name, value } = e.target;
    if (e.target.tagName === 'INPUT' && e.target.type === 'text') {
      setHeader({ ...header, [name]: toUpperCase(value) });
    } else {
      setHeader({ ...header, [name]: value });
    }
  };
  
 const handleItemChange = (idx, e) => {
  const { name, value } = e.target;
  const newItems = [...items];
  
  if (e.target.tagName === 'INPUT' && e.target.type === 'text') {
    newItems[idx][name] = toUpperCase(value);
  } else {
    newItems[idx][name] = value;
  }
  
  // Handle accepted quantity change for serial tracking
  if (name === 'quantityAdded' && newItems[idx].productTrackingType === 2) {
    const qty = parseInt(value) || 0;
    if (qty > 0) {
      setCurrentSerialItem(idx);
      // setShowSerialModal(true);
    }
  }
  
  if (name === 'quantityAdded' || name === 'rate') {
    newItems[idx].value = calculateItemValue(newItems[idx]);
  }
  
  setItems(newItems);
};
  
const handleProductChange = (idx, productCode) => {
  const newItems = [...items];
  
  if (productCode) {
    const selectedProduct = dropdownData.products.find(p => p.ProductCode === productCode);
    if (selectedProduct) {
      // Parse AllowProductTracking as integer
      const trackingType = parseInt(selectedProduct.AllowProductTracking) || 0;
      
      newItems[idx] = {
        ...newItems[idx],
        itemCode: selectedProduct.ProductCode,
        itemName: selectedProduct.ProductName,
        uom: selectedProduct.UOMName || selectedProduct.UOMID || '',
        allowBatch: selectedProduct.AllowBatchOnProduct || false,
        productTrackingType: trackingType,
        batches: [],
        serialNumbers: [],
      };
      
      // Only show modals for new items, not when editing
      if (!isEdit || !newItems[idx].StockInItemID) {
        // Reset tracking fields based on tracking type
        if (trackingType === 1) { // Batch
          setCurrentBatchItem(idx);
          setShowBatchModal(true);
        } else if (trackingType === 2) { // Serial
          newItems[idx].serialNumbers = [];
        } else { // None
          newItems[idx].batchNumber = "";
          newItems[idx].expiryDate = "";
          newItems[idx].serialNumbers = [];
        }
      }
    }
  } else {
    newItems[idx] = {
      ...newItems[idx],
      itemCode: '',
      itemName: '',
      uom: '',
      allowBatch: false,
      productTrackingType: 0,
      batchNumber: "",
      expiryDate: "",
      serialNumbers: [],
      batches: [],
    };
  }
  
  newItems[idx].value = calculateItemValue(newItems[idx]);
  setItems(newItems);
};
  
  const handleWarehouseChange = (idx, warehouseCode) => {
    const newItems = [...items];
    newItems[idx].locationCode = warehouseCode;
    setItems(newItems);
  };
  
const validate = () => {
  const errs = {};
  if (!header.stockInId || !header.stockInId.trim()) errs.stockInId = 'Required';
  if (!header.stockInDate) errs.stockInDate = 'Required';
  if (!header.addedByUserId) errs.addedByUserId = 'Required';
  if (!header.addedByUserName) errs.addedByUserName = 'Required';
  
  if (showDepartmentField && !header.department_id) errs.department_id = 'Required';
  
  items.forEach((item, idx) => {
    if (!item.itemCode) errs[`itemCode_${idx}`] = 'Required';
    if (!item.itemName) errs[`itemName_${idx}`] = 'Required';
    if (!item.uom) errs[`uom_${idx}`] = 'Required';
    if (!item.quantityAdded) errs[`quantityAdded_${idx}`] = 'Required';
    if (!item.rate) errs[`rate_${idx}`] = 'Required';
    if (!item.locationCode) errs[`locationCode_${idx}`] = 'Required';
    
    // Validate tracking fields based on tracking type
    if (item.productTrackingType === 1) { // Batch
      if (!item.batches || item.batches.length === 0) {
        errs[`batches_${idx}`] = "At least one batch is required";
      } else {
        // Check each batch has required fields
        const batchErrors = [];
        item.batches.forEach((batch, batchIdx) => {
          if (!batch.batchNumber || batch.batchNumber.trim() === '') {
            batchErrors.push(`Batch ${batchIdx + 1}: Batch number is required`);
          }
          if (!batch.expiryDate) {
            batchErrors.push(`Batch ${batchIdx + 1}: Expiry date is required`);
          }
          if (!batch.quantity || parseFloat(batch.quantity) <= 0) {
            batchErrors.push(`Batch ${batchIdx + 1}: Quantity must be greater than 0`);
          }
        });
        
        if (batchErrors.length > 0) {
          errs[`batches_${idx}`] = batchErrors.join('; ');
        }
        
        // Check if total batch quantity equals accepted quantity
        const totalBatchQty = item.batches.reduce(
          (sum, batch) => sum + (parseFloat(batch.quantity) || 0), 0
        );
        const acceptedQty = parseFloat(item.quantityAdded) || 0;
        
        if (Math.abs(totalBatchQty - acceptedQty) > 0.001) {
          errs[`batches_${idx}`] = (errs[`batches_${idx}`] ? errs[`batches_${idx}`] + '; ' : '') + 
            `Total batch quantity (${totalBatchQty}) must equal quantity (${acceptedQty})`;
        }
      }
    }
    
    if (item.productTrackingType === 2) { // Serial
      const expectedCount = parseInt(item.quantityAdded) || 0;
      if (!item.serialNumbers || item.serialNumbers.length !== expectedCount) {
        errs[`serialNumbers_${idx}`] = `All ${expectedCount} serial numbers are required`;
      }
    }
    
    if (showDepartmentItemField && !item.department_id) errs[`department_id_${idx}`] = 'Required';
  });
  
  setErrors(errs);
  return Object.keys(errs).length === 0;
};
  
 const handleSubmit = async (e) => {
  e.preventDefault();
  if (!validate() || isSubmitting) return;
  
  if (!isUserValid || !currentUser || !currentUser.UserID) {
    alert(authError || 'User information not available. Please refresh the page and log in again.');
    return;
  }
  
  setIsSubmitting(true);
  setLoading(true);
  
  try {
    const statusValue = header.status === '1' || header.status === 'Active';
    
    const payload = {
      StockInID: header.stockInId,
      AddedByUserID: parseInt(header.addedByUserId),
      AddedByUserName: header.addedByUserName,
      StockInDate: header.stockInDate,
      Note: header.note,
      Status: statusValue,
      InventoryFlag: header.inventoryFlag === 'yes',
      BranchCode: header.branchCode,
      TotalQuantity: parseInt(summary.totalQuantity),
      ...(showDepartmentField && header.department_id ? { department_id: parseInt(header.department_id) } : {}),
      ...(isEdit ? { updated_by: parseInt(currentUser.UserID) } : { created_by: parseInt(currentUser.UserID) }),
      Items: items.map(item => ({
        ItemCode: item.itemCode,
        ItemName: item.itemName,
        UOM: item.uom,
        QuantityAdded: parseInt(item.quantityAdded) || 0,
        Rate: parseFloat(item.rate) || 0,
        Value: parseFloat(item.value) || 0,
        LocationCode: item.locationCode,
        ...(showDepartmentItemField && item.department_id ? { department_id: parseInt(item.department_id) } : {}),
        // Add tracking fields
        ProductTrackingType: item.productTrackingType,
        Batches: item.productTrackingType === 1 ? 
          (item.batches || []).map(batch => ({
            BatchNumber: (batch.batchNumber || batch.BatchNumber || "").toUpperCase(),
            ExpiryDate: batch.expiryDate || batch.ExpiryDate || "",
            Quantity: parseFloat(batch.quantity || batch.Quantity || 0)
          })) : 
          [],
        SerialNumbers: item.productTrackingType === 2 ? item.serialNumbers : [],
      }))
    };
    
    const url = isEdit 
      ? `${API_URL}/stockin/${header.stockInId}`
      : `${API_URL}/stockin`;
    
    const method = isEdit ? 'PUT' : 'POST';
    
    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to save stock in record');
    }
    
    const result = await response.json();
    
    if (!isEdit && result.Items) {
      console.log('Generated item codes:', result.Items.map(item => item.StockInItemID));
    }
    
    // Delete saved items from table after successful submission
    if (!isEdit) {
      await deleteSavedItemsFromTable();
    }
    
    if (onSubmit) {
      console.log("Calling onSubmit callback"+payload);
      onSubmit();
    }
    
    onClose();
  } catch (err) {
    console.error('Error saving stock in record:', err);
    alert(err.message || 'Error saving stock in record');
  } finally {
    setIsSubmitting(false);
    setLoading(false);
  }
};


// Add these functions to your StockInFormModal component
// In the fetchSerialData function
const fetchSerialData = async (stockinId, stockinItemId, productCode, locationCode) => {
  try {
    // Validate input parameters
    if (!stockinId || !stockinItemId || !productCode || !locationCode) {
      console.error('Missing required parameters:', {
        stockinId: !!stockinId,
        stockinItemId: !!stockinItemId,
        productCode: !!productCode,
        locationCode: !!locationCode
      });
      throw new Error('Missing required parameters for fetching serial data');
    }
    
    // Ensure productCode and locationCode are strings (not arrays)
    const formattedProductCode = Array.isArray(productCode) ? productCode[0] : String(productCode).trim().toUpperCase();
    const formattedLocationCode = Array.isArray(locationCode) ? locationCode[0] : String(locationCode).trim().toUpperCase();
    
    console.log('Fetching serial data with params:', {
      stockinId: stockinId,
      stockinItemId: stockinItemId,
      productCode: formattedProductCode,
      locationCode: formattedLocationCode
    });
    
    const response = await fetch(`${API_URL}/stockin/${stockinId}/items/${stockinItemId}/serials?productCode=${encodeURIComponent(formattedProductCode)}&locationCode=${encodeURIComponent(formattedLocationCode)}`);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API response not OK:', response.status, response.statusText, errorText);
      throw new Error(`Failed to fetch serial data: ${response.status} - ${errorText}`);
    }
    
    const data = await response.json();
    console.log('Fetched serial data:', data);
    return data;
  } catch (error) {
    console.error('Error fetching serial data:', error);
    throw error;
  }
};

// Apply the same fix to fetchBatchData
const fetchBatchData = async (stockinId, stockinItemId, productCode, locationCode) => {
  try {
    // Validate input parameters
    if (!stockinId || !stockinItemId || !productCode || !locationCode) {
      console.error('Missing required parameters:', {
        stockinId: !!stockinId,
        stockinItemId: !!stockinItemId,
        productCode: !!productCode,
        locationCode: !!locationCode
      });
      throw new Error('Missing required parameters for fetching batch data');
    }
    
    // Ensure productCode and locationCode are strings (not arrays)
    const formattedProductCode = Array.isArray(productCode) ? productCode[0] : String(productCode).trim().toUpperCase();
    const formattedLocationCode = Array.isArray(locationCode) ? locationCode[0] : String(locationCode).trim().toUpperCase();
    
    console.log('Fetching batch data with params:', {
      stockinId: stockinId,
      stockinItemId: stockinItemId,
      productCode: formattedProductCode,
      locationCode: formattedLocationCode
    });
    
    const response = await fetch(`${API_URL}/stockin/${stockinId}/items/${stockinItemId}/batch?productCode=${encodeURIComponent(formattedProductCode)}&locationCode=${encodeURIComponent(formattedLocationCode)}`);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API response not OK:', response.status, response.statusText, errorText);
      throw new Error(`Failed to fetch batch data: ${response.status} - ${errorText}`);
    }
    
    const data = await response.json();
    console.log('Fetched batch data:', data);
    return data;
  } catch (error) {
    console.error('Error fetching batch data:', error);
    throw error;
  }
};


const handleSaveBatches = (batches) => {
  if (currentBatchItem !== null) {
    const newItems = [...items];
    // Format the batches to ensure expiry date is in the correct format
    const formattedBatches = batches.map(batch => ({
      ...batch,
      expiryDate: batch.expiryDate ? new Date(batch.expiryDate).toISOString().split('T')[0] : ""
    }));
    newItems[currentBatchItem] = {
      ...newItems[currentBatchItem],
      batches: formattedBatches
    };
    setItems(newItems);
    setShowBatchModal(false);
    setCurrentBatchItem(null);
  }
};

const handleSaveSerial = (serialNumbers) => {
  if (currentSerialItem !== null) {
    const newItems = [...items];
    newItems[currentSerialItem] = {
      ...newItems[currentSerialItem],
      serialNumbers
    };
    setItems(newItems);
    setShowSerialModal(false);
    setCurrentSerialItem(null);
  }
};
  
  if (!open) return null;
  
  return (
    <>
      <div className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
        <style>
          {`
            .hide-scrollbar {
              -ms-overflow-style: none;
              scrollbar-width: none;
            }
            .hide-scrollbar::-webkit-scrollbar {
              display: none;
            }
            .custom-scrollbar::-webkit-scrollbar {
              height: 8px;
            }
            .custom-scrollbar::-webkit-scrollbar-track {
              background: #f1f5f9;
              border-radius: 10px;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb {
              background: #cbd5e1;
              border-radius: 10px;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb:hover {
              background: #94a3b8;
            }
            .uppercase-input {
              text-transform: uppercase;
            }
            button:focus, select:focus, input:focus, textarea:focus {
              outline: 2px solid #4299e1;
              outline-offset: 2px;
            }
            .saved-item-badge {
              display: inline-flex;
              align-items: center;
              background-color: #e6f7ff;
              color: #1890ff;
              font-size: 10px;
              padding: 2px 6px;
              border-radius: 10px;
              margin-left: 4px;
            }
            .autofill-button {
              background-color: #f0f9ff;
              color: #0284c7;
              border-color: #0284c7;
            }
            .autofill-button:hover {
              background-color: #e0f2fe;
            }
            .shortcut-key {
              display: inline-block;
              background-color: #f1f5f9;
              color: #334155;
              font-family: monospace;
              font-size: 0.75rem;
              padding: 0.125rem 0.25rem;
              border-radius: 0.25rem;
              border: 1px solid #e2e8f0;
              margin: 0 0.125rem;
            }
          `}
        </style>
        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
        <div className={`bg-white shadow-2xl w-full flex flex-col transition-transform transform-gpu duration-300 ease-in-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        } ${
          isMaximized ? 'h-full max-w-full' : 'h-full max-w-6xl'
        }`} tabIndex={0}>
          <div className="flex-shrink-0 p-8 pb-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-[#384959]">{isEdit ? 'Edit Stock In Record' : 'Create Stock In Record'}</h2>
              <div className="flex items-center gap-2">
                <button 
                  className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" 
                  onClick={toggleMaximize}
                  title={isMaximized ? "Minimize" : "Maximize"}
                >
                  {isMaximized ? <MinimizeIcon size={20} /> : <MaximizeIcon size={20} />}
                </button>
                <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" onClick={onClose}>
                  <CloseIcon size={24} />
                </button>
              </div>
            </div>
            
            {/* <div className="mt-4 text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
              <div className="font-medium mb-1">Keyboard Shortcuts:</div>
              <div className="grid grid-cols-2 gap-1">
                <div><span className="shortcut-key">Alt+P</span> - Multi-select products</div>
                <div><span className="shortcut-key">Alt+W</span> - Multi-select warehouses</div>
                <div><span className="shortcut-key">Ctrl+F2</span> - Add new item</div>
                <div><span className="shortcut-key">Ctrl+F3</span> - Remove last item</div>
                <div><span className="shortcut-key">Ctrl+P</span> - Multi-select in dropdown</div>
                <div><span className="shortcut-key">Ctrl+W</span> - Multi-select in dropdown</div>
              </div>
              <div className="mt-2 font-medium">ERP Commands:</div>
              <div><span className="shortcut-key">SAVE_POPUP_ITEMS</span> - Save selected items to table</div>
              <div><span className="shortcut-key">LOAD_SAVED_ITEMS</span> - Load saved items from table</div>
              <div><span className="shortcut-key">AUTOFILL_ITEMS</span> - Autofill table with selected items</div>
              <div className="mt-2 font-medium">Multi-Select Modal Commands:</div>
              <div><span className="shortcut-key">Ctrl+Enter</span> - Autofill table with selected products</div>
            </div> */}
          </div>
          
          {open && !isUserValid && (
            <div className="mx-8 mb-4 bg-red-50 border-l-4 border-red-500 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">
                    {authError || 'User information not available. Please refresh the page and log in again.'}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex-grow overflow-y-auto hide-scrollbar px-8">
            <form onSubmit={handleSubmit} className="space-y-6 pb-6">
              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <h3 className="text-lg font-semibold text-[#384959] mb-4">Stock In Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Stock In ID *</label>
                    <input 
                      name="stockInId" 
                      value={header.stockInId} 
                      onChange={handleHeaderChange} 
                      disabled
                      className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase-input"
                    />
                    {errors.stockInId && <span className="text-xs text-red-500">{errors.stockInId}</span>}
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Stock In Date *</label>
                    <input 
                      type="date" 
                      name="stockInDate" 
                      value={header.stockInDate} 
                      onChange={handleHeaderChange} 
                      className={`w-full border rounded-lg px-3 py-2 ${errors.stockInDate ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]`}
                    />
                    {errors.stockInDate && <span className="text-xs text-red-500">{errors.stockInDate}</span>}
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Added By User ID *</label>
                    <input 
                      name="addedByUserId" 
                      value={header.addedByUserId} 
                      onChange={handleHeaderChange}
                      readOnly
                      className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]"
                      title="Auto-populated from logged-in user"
                    />
                    {errors.addedByUserId && <span className="text-xs text-red-500">{errors.addedByUserId}</span>}
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Added By User Name *</label>
                    <input 
                      name="addedByUserName" 
                      value={header.addedByUserName} 
                      onChange={handleHeaderChange}
                      readOnly
                      className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase"
                      title="Auto-populated from logged-in user"
                    />
                    {errors.addedByUserName && <span className="text-xs text-red-500">{errors.addedByUserName}</span>}
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Branch</label>
                    <select 
                      name="branchCode" 
                      value={header.branchCode} 
                      onChange={handleHeaderChange} 
                      className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]"
                    >
                      <option value="">Select Branch</option>
                      {dropdownData.branches && dropdownData.branches.map(branch => (
                        <option key={branch.BranchCode} value={branch.BranchCode}>
                          {branch.BranchCode} - {branch.BranchName}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {showDepartmentField && (
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Department *
                      </label>
                      <select
                        name="department_id"
                        value={header.department_id}
                        onChange={handleHeaderChange}
                        className={`w-full border rounded-lg px-3 py-2 ${
                          errors.department_id ? "border-red-400" : "border-[#E2E8F0]"
                        } focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase`}
                        disabled={loadingDepartments}
                      >
                        <option value="">{loadingDepartments ? 'Loading departments...' : 'Select Department'}</option>
                        {departments.map((dept) => (
                          <option key={dept.DepartmentID} value={dept.DepartmentID}>
                            {dept.DepartmentCode} - {dept.DepartmentName}
                          </option>
                        ))}
                      </select>
                      {errors.department_id && (
                        <span className="text-xs text-red-500">
                          {errors.department_id}
                        </span>
                      )}
                    </div>
                  )}
                  
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Status</label>
                    <select 
                      name="status" 
                      value={header.status} 
                      onChange={handleHeaderChange} 
                      className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]"
                    >
                      {STATUS_OPTIONS.map(status => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Add to Inventory *</label>
                    <select 
                      name="inventoryFlag" 
                      value={header.inventoryFlag} 
                      onChange={handleHeaderChange} 
                      className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]"
                    >
                      {INVENTORY_FLAG_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Select "Yes" to add items to inventory, "No" to skip inventory update</p>
                  </div>
                  <div className="flex flex-col gap-2 md:col-span-2">
                    <label className="block text-sm font-medium text-[#64748b]">Note</label>
                    <textarea 
                      name="note" 
                      value={header.note} 
                      onChange={handleHeaderChange} 
                      rows={3}
                      className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase-input"
                    />
                  </div>
                </div>
              </div>
              
              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-[#384959]">Stock In Items</h3>
                  <div className="flex gap-2">
                    {savedItems.length > 0 && (
                      <button 
                        type="button" 
                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm flex items-center"
                        onClick={loadSavedItemsFromTable}
                        title="Load saved items from table"
                      >
                        <SaveIcon className="mr-1" size={12} /> Reload Saved
                      </button>
                    )}
                    <button 
                      type="button" 
                      className="px-4 py-2 bg-[#6A89A7] text-white rounded-lg hover:bg-[#88BDF2] flex items-center transition-colors" 
                      onClick={addItem}
                    >
                      <PlusIcon className="mr-1" /> Add Item
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto custom-scrollbar">
                  <table className="min-w-full border border-[#E2E8F0]">
                    <thead>
                      <tr className="bg-[#BDDDFC] text-[#384959]">
                        <th className="p-2">#</th>
                        <th className="p-2">Product</th>
                        <th className="p-2">Product Code</th>
                        <th className="p-2">Item Name</th>
                        <th className="p-2">UOM</th>
                        <th className="p-2">Quantity</th>
                        <th className="p-2">Rate</th>
                        <th className="p-2">Value</th>
                        <th className="p-2">Warehouse</th>
                        {showDepartmentItemField && <th className="p-2">Department</th>}
                     <th className="p-2">Tracking Type</th>
<th className="p-2">Batch/Serial Info</th>
                        <th className="p-2">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, idx) => (
                        <tr key={idx} className="border-t border-[#E2E8F0]">
                          <td className="p-2 text-center">{idx + 1}</td>
                          <td className="p-2">
                            <ProductSearchDropdown
                              products={dropdownData.products}
                              value={item.itemCode}
                              onChange={(productCode) => handleProductChange(idx, productCode)}
                              onMultiSelectTrigger={() => setShowMultiSelectModal(true)}
                              error={errors[`itemCode_${idx}`]}
                            />
                          </td>
                          <td className="p-2">
                            <div className="flex items-center">
                              <input 
                                name="itemCodeDisplay" 
                                value={item.itemCode} 
                                readOnly 
                                className="w-24 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9] uppercase-input"
                                title="Auto-populated from product selection"
                              />
                              {item.savedToTable && (
                                <span className="saved-item-badge">Saved</span>
                              )}
                            </div>
                          </td>
                          <td className="p-2">
                            <input 
                              name="itemName" 
                              value={item.itemName} 
                              onChange={e => handleItemChange(idx, e)} 
                              className={`w-40 border rounded-lg px-2 py-1 uppercase-input ${errors[`itemName_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]`}
                            />
                            {errors[`itemName_${idx}`] && <span className="text-xs text-red-500">*</span>}
                          </td>
                          <td className="p-2">
                            <input 
                              name="uom" 
                              value={item.uom} 
                              readOnly 
                              className="w-16 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9] uppercase"
                              title="Auto-populated from product selection"
                            />
                            {errors[`uom_${idx}`] && <span className="text-xs text-red-500">*</span>}
                          </td>
                          <td className="p-2">
                            <input 
                              name="quantityAdded" 
                              type="number" 
                              min="0" 
                              value={item.quantityAdded} 
                              onChange={e => handleItemChange(idx, e)} 
                              className={`w-16 border rounded-lg px-2 py-1 ${errors[`quantityAdded_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]`}
                            />
                            {errors[`quantityAdded_${idx}`] && <span className="text-xs text-red-500">*</span>}
                          </td>
                          <td className="p-2">
                            <input 
                              name="rate" 
                              type="number" 
                              min="0" 
                              step="0.01"
                              value={item.rate} 
                              onChange={e => handleItemChange(idx, e)} 
                              className={`w-20 border rounded-lg px-2 py-1 ${errors[`rate_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]`}
                            />
                            {errors[`rate_${idx}`] && <span className="text-xs text-red-500">*</span>}
                          </td>
                          <td className="p-2">
                            <input 
                              name="value" 
                              value={item.value} 
                              readOnly 
                              className="w-20 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9]"
                            />
                          </td>
                          <td className="p-2">
                            <WarehouseSearchDropdown
                              warehouses={dropdownData.locations}
                              value={item.locationCode}
                              onChange={(warehouseCode) => handleWarehouseChange(idx, warehouseCode)}
                              onMultiSelectTrigger={() => setShowWarehouseMultiSelectModal(true)}
                              error={errors[`locationCode_${idx}`]}
                            />
                          </td>
                          {showDepartmentItemField && (
                            <td className="p-2">
                              <select
                                name="department_id"
                                value={item.department_id}
                                onChange={(e) => handleItemChange(idx, e)}
                                className={`w-24 border rounded-lg px-2 py-1 ${
                                  errors[`department_id_${idx}`]
                                    ? "border-red-400"
                                    : "border-[#E2E8F0]"
                                } focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] uppercase`}
                              >
                                <option value="">Select</option>
                                {departments.map((dept) => (
                                  <option key={dept.DepartmentID} value={dept.DepartmentID}>
                                    {dept.DepartmentCode}
                                  </option>
                                ))}
                              </select>
                              {errors[`department_id_${idx}`] && (
                                <span className="text-xs text-red-500">*</span>
                              )}
                            </td>
                          )}
                          <td className="p-2">
  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
    item.productTrackingType === 0 ? 'bg-gray-100 text-gray-800' :
    item.productTrackingType === 1 ? 'bg-blue-100 text-blue-800' :
    'bg-green-100 text-green-800'
  }`}>
    {item.productTrackingType === 0 ? 'None' :
     item.productTrackingType === 1 ? 'Batch' : 'Serial'}
  </span>
</td>
                         <td className="p-2">
  {item.productTrackingType === 1 && (
    <div>
      <div className="text-xs font-medium">
        {item.batches?.length || 0} batch{item.batches?.length !== 1 ? 'es' : ''}
      </div>
      <button
        type="button"
        className="text-blue-600 hover:text-blue-800 text-xs mt-1"
        onClick={() => {
          setCurrentBatchItem(idx);
          setShowBatchModal(true);
        }}
      >
        Edit Batches
      </button>
    </div>
    )}
  {item.productTrackingType === 2 && (
    <div>
      <div className="text-xs font-medium">
        {item.serialNumbers?.length || 0} of {item.quantityAdded} serials
      </div>
      <button
        type="button"
        className="text-blue-600 hover:text-blue-800 text-xs mt-1"
        onClick={() => {
          setCurrentSerialItem(idx);
          setShowSerialModal(true);
        }}
      >
        Edit Serials
      </button>
    </div>
  )}
   {item.productTrackingType === 0 && (
    <span className="text-gray-400 text-sm">No tracking</span>
  )}
</td>

                          <td className="p-2 text-center">
                            <button 
                              type="button" 
                              className="text-red-500 hover:text-red-700 transition-colors" 
                              onClick={() => removeItem(idx)} 
                              disabled={items.length === 1}
                            >
                              <TrashIcon />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <div className="mt-6 bg-white p-4 rounded-lg border border-[#E2E8F0]">
                  <h4 className="text-md font-semibold text-[#384959] mb-4">Total Summary</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Total Quantity</span>
                      <span className="text-base font-semibold">{summary.totalQuantity}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Total Value</span>
                      <span className="text-base font-semibold text-lg text-blue-600">{summary.totalValue}</span>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
          
          <div className="flex-shrink-0 p-8 pt-0">
            <div className="flex justify-end gap-3">
              <button 
                type="button" 
                className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]" 
                onClick={onClose}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="px-6 py-3 rounded-lg text-white font-semibold shadow-lg transition-all duration-300 ease-in-out transform hover:scale-105 bg-[#6A89A7] hover:bg-[#88BDF2] disabled:opacity-50 disabled:cursor-not-allowed" 
                disabled={loading || isSubmitting || !isUserValid}
                onClick={handleSubmit}
              >
                {loading || isSubmitting ? 'Saving...' : (isEdit ? 'Update Stock In' : 'Save Stock In')}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <ProductMultiSelectModal
        open={showMultiSelectModal}
        onClose={() => setShowMultiSelectModal(false)}
        products={dropdownData.products}
        onConfirm={handleMultiSelectProducts}
        onSaveToTable={savePopupItemsToTable}
        onAutofill={autofillSelectedProducts}
      />
      
      <WarehouseMultiSelectModal
        open={showWarehouseMultiSelectModal}
        onClose={() => setShowWarehouseMultiSelectModal(false)}
        warehouses={dropdownData.locations}
        onConfirm={handleMultiSelectWarehouses}
      />

   
<BatchTableModal
  isOpen={showBatchModal}
  onClose={() => {
    setShowBatchModal(false);
    setCurrentBatchItem(null);
  }}
  onSave={handleSaveBatches}
  initialBatches={currentBatchItem !== null ? items[currentBatchItem]?.batches || [] : []}
  productName={currentBatchItem !== null ? items[currentBatchItem]?.itemName : ""}
  acceptedQuantity={currentBatchItem !== null ? parseFloat(items[currentBatchItem]?.quantityAdded) || 0 : 0}
/>


<SerialModal
  isOpen={showSerialModal}
  onClose={() => {
    setShowSerialModal(false);
    setCurrentSerialItem(null);
  }}
  onSave={handleSaveSerial}
  count={currentSerialItem !== null ? parseInt(items[currentSerialItem]?.quantityAdded) || 0 : 0}
  initialSerialNumbers={currentSerialItem !== null ? items[currentSerialItem]?.serialNumbers : []}
  productName={currentSerialItem !== null ? items[currentSerialItem]?.itemName : ""}
  locationCode={currentSerialItem !== null ? items[currentSerialItem]?.locationCode : ""}
/>
    </>
  );
};

export default StockInFormModal;